import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt, faCheckCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import IMEISubmissionModal from './IMEISubmissionModal';
import { sellerAPI } from '../services/api';

// One IMEI-entry point per order, covering every line item — an order can
// have several products (each with its own quantity), so this lists each
// item still needing IMEI and opens IMEISubmissionModal (sized to that
// item's quantity) one at a time, rather than a separate always-visible
// button per row cluttering the orders table.
//
// This used to render as a bare icon with no label, indistinguishable from
// the decorative icons around it — sellers had no way to know it was both
// interactive and a required fulfilment step. It now reads as an explicit
// amber call-to-action with the pending device count on it, and pulses until
// the IMEIs are in.
const OrderIMEIButton = ({ order, showCustomToast, isDarkMode = false, compact = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [activeItem, setActiveItem] = useState(null); // { id, product_title, quantity }
  const [submittedIds, setSubmittedIds] = useState([]); // items just submitted this session

  const items = order.items || [];
  const pendingItems = items.filter(item => !item.imei_submitted && !submittedIds.includes(item.id || item._id));

  if (items.length === 0 || pendingItems.length === 0) return null;

  // Devices, not line items — three units of one listing is three IMEIs, and
  // that is the number the seller actually has to type in.
  const pendingDevices = pendingItems.reduce((n, item) => n + (item.quantity || 1), 0);

  const handleSubmit = async (imeis) => {
    const itemId = activeItem.id || activeItem._id;
    await sellerAPI.submitOrderItemIMEI(order.id || order._id, itemId, imeis);
    setSubmittedIds(prev => [...prev, itemId]);
    setActiveItem(null);
    showCustomToast?.({ type: 'success', message: 'IMEI submitted successfully' });
    if (pendingItems.length <= 1) setShowPicker(false);
  };

  const openEntry = () => (pendingItems.length === 1 ? setActiveItem(pendingItems[0]) : setShowPicker(true));

  const c = {
    surface: isDarkMode ? '#16161d' : '#ffffff',
    border: isDarkMode ? 'rgba(255,255,255,0.09)' : '#e2e8f0',
    text: isDarkMode ? '#f1f5f9' : '#0f172a',
    muted: isDarkMode ? '#94a3b8' : '#64748b',
    raised: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
  };

  return (
    <>
      {/* The pulse keyframes are scoped here so this component stays
          self-contained — it's mounted inside two different dashboards whose
          stylesheets have no shared home for it. */}
      <style>{`
        @keyframes autoboy-imei-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.45); }
          50%      { box-shadow: 0 0 0 6px rgba(245,158,11,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .autoboy-imei-cta { animation: none !important; }
        }
      `}</style>

      <button
        className="autoboy-imei-cta"
        title={`Enter IMEI — ${pendingDevices} device${pendingDevices > 1 ? 's' : ''} in this order still need${pendingDevices > 1 ? '' : 's'} one, required before shipping`}
        aria-label={`Enter IMEI for ${pendingDevices} pending device${pendingDevices > 1 ? 's' : ''}`}
        onClick={openEntry}
        style={compact ? {
          // Icon-only footprint matching the other 32×32 IconBtn actions
          // this sits beside in a table's Actions column — the full pill
          // (icon + "Enter IMEI" text + count) is wider than the whole
          // column on any but the widest screens. Discoverability is kept
          // via the tooltip, the pulse, and the count badge overlaid on the
          // corner instead of inline.
          position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
          border: '1px solid rgba(245,158,11,0.45)',
          background: isDarkMode ? 'rgba(245,158,11,0.16)' : 'rgba(245,158,11,0.12)',
          color: isDarkMode ? '#fbbf24' : '#b45309',
          fontSize: '0.9rem',
          animation: 'autoboy-imei-pulse 2.4s ease-in-out infinite',
        } : {
          display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
          padding: '0.45rem 0.8rem',
          borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap',
          border: '1px solid rgba(245,158,11,0.45)',
          background: isDarkMode ? 'rgba(245,158,11,0.16)' : 'rgba(245,158,11,0.12)',
          color: isDarkMode ? '#fbbf24' : '#b45309',
          fontWeight: 700, fontSize: '0.78rem', lineHeight: 1,
          animation: 'autoboy-imei-pulse 2.4s ease-in-out infinite',
        }}
      >
        <FontAwesomeIcon icon={faMobileAlt} />
        {!compact && <span>Enter IMEI</span>}
        <span style={compact ? {
          position: 'absolute', top: '-6px', right: '-6px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '16px', height: '16px', padding: '0 3px', borderRadius: '99px',
          background: '#f59e0b', color: '#fff', fontSize: '0.62rem', fontWeight: 800,
          border: '2px solid ' + (isDarkMode ? '#16161d' : '#fff'), lineHeight: 1,
        } : {
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '17px', height: '17px', padding: '0 4px', borderRadius: '99px',
          background: '#f59e0b', color: '#fff', fontSize: '0.68rem', fontWeight: 800,
        }}>
          {pendingDevices}
        </span>
      </button>

      {showPicker && !activeItem && (
        <div
          onClick={() => setShowPicker(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.72)', backdropFilter: 'blur(3px)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto', overscrollBehavior: 'contain' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: c.surface, color: c.text, borderRadius: '18px', padding: '1.4rem',
              width: '100%', maxWidth: '440px', maxHeight: 'calc(100dvh - 2rem)', margin: 'auto', overflowY: 'auto',
              border: `1px solid ${c.border}`, boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Which item?</h3>
              <button onClick={() => setShowPicker(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.muted, fontSize: '1rem' }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <p style={{ margin: '0 0 1.1rem', fontSize: '0.82rem', color: c.muted, lineHeight: 1.5 }}>
              This order has more than one product. Enter the IMEI for each one — pick an item to start.
            </p>

            {items.map(item => {
              const id = item.id || item._id;
              const done = item.imei_submitted || submittedIds.includes(id);
              return (
                <button
                  key={id}
                  disabled={done}
                  onClick={() => setActiveItem(item)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                    width: '100%', padding: '0.85rem', marginBottom: '0.55rem', borderRadius: '11px',
                    border: `1px solid ${done ? 'rgba(34,197,94,0.35)' : c.border}`,
                    background: done ? (isDarkMode ? 'rgba(34,197,94,0.08)' : '#f0fdf4') : c.raised,
                    color: c.text, cursor: done ? 'default' : 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.product_title || 'Product'}
                    </span>
                    <span style={{ fontSize: '0.76rem', color: c.muted }}>
                      {item.quantity || 1} device{(item.quantity || 1) > 1 ? 's' : ''}
                    </span>
                  </span>
                  {done
                    ? <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22C55E', flexShrink: 0 }} />
                    : <span style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: 800, flexShrink: 0 }}>PENDING</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeItem && (
        <IMEISubmissionModal
          unitCount={activeItem.quantity || 1}
          title={activeItem.product_title || 'Enter IMEI'}
          isDarkMode={isDarkMode}
          onSubmit={handleSubmit}
          onClose={() => setActiveItem(null)}
        />
      )}
    </>
  );
};

export default OrderIMEIButton;
